import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Product, PRODUCTS } from '../data/products';
import { resolveImageUrl } from '../assets/images';

interface ProductContextType {
  products: Product[];
  isLoading: boolean;
  selectedProductId: string | null;
  setSelectedProductId: (id: string | null) => void;
  getProductById: (id: string) => Product | undefined;
  addProduct: (productData: Omit<Product, 'id'> & { id?: string }) => Promise<Product>;
  updateProduct: (id: string, updatedFields: Partial<Product>) => Promise<Product | void>;
  deleteProduct: (id: string) => Promise<boolean>;
  resetToDefaultProducts: () => Promise<void>;
  refreshProducts: () => Promise<void>;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

function sanitizeProduct(item: any): Product {
  return {
    id: String(item.id || `product-${Date.now()}`),
    name: String(item.name || 'Commercial Equipment'),
    tagline: String(item.tagline || 'High-precision commercial machinery'),
    description: String(item.description || 'Reliable commercial food-grade equipment.'),
    category: item.category || 'water-purification',
    capacity: String(item.capacity || '100 Litres'),
    material: String(item.material || 'Food-Grade Stainless Steel AISI 304'),
    power: String(item.power || '240V AC 50Hz'),
    priceKsh: typeof item.priceKsh === 'number' ? item.priceKsh : Number(item.priceKsh) || 50000,
    originalPriceKsh: item.originalPriceKsh ? Number(item.originalPriceKsh) : undefined,
    features: Array.isArray(item.features) ? item.features : [],
    specs: Array.isArray(item.specs) ? item.specs : [],
    image: resolveImageUrl(item.image, item.category),
    inStock: item.inStock !== false,
    seo: {
      metaTitle: item.seo?.metaTitle || `${item.name || 'Equipment'} | Saints Tech Solutions`,
      metaDescription: item.seo?.metaDescription || item.tagline || item.description || '',
      keywords: item.seo?.keywords || `${item.name || 'Equipment'}, Saints Tech Solutions, Kenya`,
      tags: Array.isArray(item.seo?.tags) ? item.seo.tags : [item.category || 'machinery', 'Kenya'],
    },
  };
}

export const ProductProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Start with default products as instant initial state to avoid layout flashes
  const [products, setProducts] = useState<Product[]>(PRODUCTS);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [selectedProductId, setSelectedProductId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const pathname = window.location.pathname.replace(/^\/+|\/+$/g, '');
    if (pathname.startsWith('product/')) {
      return pathname.replace('product/', '').trim() || null;
    }
    if (pathname.startsWith('products/')) {
      const id = pathname.replace('products/', '').trim();
      return id || null;
    }
    const hash = window.location.hash;
    if (hash.startsWith('#product/')) {
      return hash.replace('#product/', '').trim() || null;
    }
    return null;
  });

  // Fetch all products directly from the database API
  const refreshProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await fetch('/api/products');
      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.products)) {
          const sanitized = data.products.map(sanitizeProduct);
          setProducts(sanitized);
        }
      }
    } catch (err) {
      console.warn('Could not fetch products from database API, using initial catalog:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshProducts();
  }, [refreshProducts]);

  const getProductById = (id: string): Product | undefined => {
    return products.find((p) => p.id === id);
  };

  // Add new product into the database
  const addProduct = async (productData: Omit<Product, 'id'> & { id?: string }): Promise<Product> => {
    const generatedId =
      productData.id ||
      productData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '') + `-${Date.now().toString().slice(-4)}`;

    const newProduct: Product = {
      ...productData,
      id: generatedId,
      image: resolveImageUrl(productData.image, productData.category),
      seo: productData.seo || {
        metaTitle: `${productData.name} | Saints Tech Solutions Kenya`,
        metaDescription: productData.tagline || productData.description.slice(0, 150),
        keywords: `${productData.name}, Saints Tech Solutions, Kenya, Nairobi, commercial vending`,
        tags: [productData.category, 'Kenya', 'Saints Tech'],
      },
    };

    // Optimistic UI update
    setProducts((prev) => [newProduct, ...prev]);

    // Persist to server database
    try {
      const res = await fetch('/api/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newProduct)
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.product) {
          const savedSanitized = sanitizeProduct(data.product);
          setProducts((prev) => prev.map((p) => (p.id === newProduct.id ? savedSanitized : p)));
          return savedSanitized;
        }
      }
    } catch (err) {
      console.error('Failed to save product to database:', err);
    }

    return newProduct;
  };

  // Update existing product in the database
  const updateProduct = async (id: string, updatedFields: Partial<Product>): Promise<Product | void> => {
    let targetUpdated: Product | undefined;

    setProducts((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const effectiveImage =
            updatedFields.image !== undefined
              ? resolveImageUrl(updatedFields.image, updatedFields.category || item.category)
              : item.image;

          targetUpdated = {
            ...item,
            ...updatedFields,
            image: effectiveImage,
            seo: {
              ...(item.seo || {}),
              ...(updatedFields.seo || {}),
            },
          };
          return targetUpdated;
        }
        return item;
      })
    );

    // Persist to database
    try {
      const res = await fetch(`/api/products/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedFields)
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.product) {
          const saved = sanitizeProduct(data.product);
          setProducts((prev) => prev.map((p) => (p.id === id ? saved : p)));
          return saved;
        }
      }
    } catch (err) {
      console.error('Failed to update product in database:', err);
    }

    return targetUpdated;
  };

  // Delete product from the database
  const deleteProduct = async (id: string): Promise<boolean> => {
    setProducts((prev) => prev.filter((p) => p.id !== id));
    if (selectedProductId === id) {
      setSelectedProductId(null);
    }

    try {
      const res = await fetch(`/api/products/${encodeURIComponent(id)}`, {
        method: 'DELETE'
      });
      return res.ok;
    } catch (err) {
      console.error('Failed to delete product from database:', err);
      return false;
    }
  };

  // Reset products back to database factory defaults
  const resetToDefaultProducts = async (): Promise<void> => {
    try {
      const res = await fetch('/api/products/reset', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.products)) {
          setProducts(data.products.map(sanitizeProduct));
          return;
        }
      }
    } catch (err) {
      console.error('Failed to reset products in database:', err);
    }
    setProducts(PRODUCTS);
  };

  return (
    <ProductContext.Provider
      value={{
        products,
        isLoading,
        selectedProductId,
        setSelectedProductId,
        getProductById,
        addProduct,
        updateProduct,
        deleteProduct,
        resetToDefaultProducts,
        refreshProducts,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};
